import { useEffect, useMemo } from 'react';

/* Draft persistence for long forms.
 *
 * The new-ticket form holds a lot of typed input across a lot of controls, and
 * anything that remounts it (a reload, an auth bounce, navigating out to add a
 * CRM record and coming back) used to throw all of it away. Values are mirrored
 * into sessionStorage — per tab, never sent anywhere — and read back on mount.
 */

/** Read a saved draft. Call once, lazily, when seeding useState. */
export function readFormDraft<T extends Record<string, unknown>>(storageKey: string): Partial<T> {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Partial<T>) : {};
  } catch {
    return {};
  }
}

export function clearFormDraft(storageKey: string): void {
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    /* private mode / quota — nothing to clean up */
  }
}

/** Mirror the current values into sessionStorage, debounced so typing doesn't
 *  serialize on every keystroke. */
export function usePersistFormDraft(
  storageKey: string,
  values: Record<string, unknown>,
  enabled = true,
): void {
  const serialized = useMemo(() => {
    try {
      return JSON.stringify(values);
    } catch {
      return null;
    }
  }, [values]);

  useEffect(() => {
    if (serialized === null) return;
    // An untouched form shouldn't leave a draft behind — otherwise the next
    // visit claims to have restored something when there was nothing to keep.
    if (!enabled) {
      clearFormDraft(storageKey);
      return;
    }
    const t = setTimeout(() => {
      try {
        sessionStorage.setItem(storageKey, serialized);
      } catch {
        /* quota exceeded — a lost draft beats a thrown render */
      }
    }, 300);
    return () => clearTimeout(t);
  }, [storageKey, serialized, enabled]);
}
